import forEach from "core/src/core/util/for-each.js";
import { introAlpha } from "../../shared/round-intro";
import {wormColors} from "core/src/core/constants.js";

import ScaledCanvasContext from "../scaled-canvas-context.js";
import { drawTrailFade, trailPoints, TRAIL_FADE_SECONDS } from "./trail-fade.js";

// Trails are baked as CONTINUOUS polylines, not one round-capped stroke per segment: with ~2.5px
// segments (STEP) a per-segment stroke leaves an anti-aliasing seam at every joint, which reads as a
// bumpy, beaded edge (very visible on a finely-segmented client-authoritative worm; the server's
// coarser copy looks smooth). So finalized segments are flushed to the persistent main canvas a whole
// CHUNK at a time, each chunk one continuous stroke - the main canvas gets ~1 seam per CHUNK instead
// of per segment. Below CHUNK segments, they're still redrawn live on the secondary canvas each frame
// (connecting to the currently-growing edge), which is the only reason this trades off against redraw
// cost at all: a bigger CHUNK means fewer permanent-canvas seams but a bigger live-redrawn backlog.
var TRAIL_CHUNK = 16;

// Bakes are batched: every bake modifies the persistent main canvas, and a modified
// canvas is copied out for compositing that frame (a full-canvas copy per frame on
// software-rendered canvases). Baking whenever any one of 20 worms filled a chunk
// touched the main canvas most frames; now all due bakes go out together at most
// every BAKE_INTERVAL_MS. A tail that outgrows MAX_TAIL segments bakes regardless,
// so the live-redrawn backlog stays bounded.
var BAKE_INTERVAL_MS = 250;
var MAX_TAIL = TRAIL_CHUNK * 4;
// A wall-hack wrap track ("<track>#N") whose last segment ended this long before
// the worm's render clock is finished for now: bake it whole (its last segment too)
// so it stops being redrawn, and stops adding a dirty rect, every frame. Same for a
// track that ends in a death marker, once the worm's clock has reached it (a
// replay holds the whole round from its first frame, deaths still to come).
var WRAP_IDLE_SEC = 0.5;
function isFinishedTrack(segments, trackId, wt) {
    var last = segments[segments.length - 1];
    if (last.type === "worm_died") return last.startTime <= wt;
    return trackId.indexOf("#") !== -1 && last.startTime + (last.duration || 0) < wt - WRAP_IDLE_SEC;
}

// A jump / still_arc / zero-speed segment is already a hard break in drawRuns below (it closes
// the run and resets the connecting point) - a REAL gap the player can see, not an artifact. Baking
// straight through one costs no seam at all, so it's a free place to flush the backlog early instead
// of waiting for a full CHUNK to accumulate.
function isFreeCheckpoint(seg) {
    return !!(seg.jump || seg.type === "still_arc" || seg.speed <= 0);
}

// `getWormRenderTime(trackId)` (optional): per-worm render clocks (online). Each
// opponent renders on its own latency-sized clock (adaptive delay), and the LOCAL
// player's own track resolves to the predicted "now", so this renderer draws the own
// tail straight to the predicted head - the own worm needs no separate lead pass.
// Falsy/undefined falls back to the shared frame renderTime.
// `introDelay(playerId)` (optional): the round-start roll call (round-intro.ts).
// A worm's trail is not drawn (nor baked) until its head has faded in; the
// zero-length first segment would otherwise show as a dot at the spawn spot.
export default function WormBodyRenderer({ gameState, players, mainCanvas, secondaryCanvas, canvasState, scale=1, getWormRenderTime, introDelay }) {
    var mainContext = mainCanvas.getContext("2d");
    var scaledMainContext = ScaledCanvasContext(mainContext, scale);
    var secondaryContext = secondaryCanvas.getContext("2d");
    var scaledSecondaryContext = ScaledCanvasContext(secondaryContext, scale);

    var wormRenderData = {};
    var prevRenderTime = 0;
    var lastBakeAt = -Infinity;

    // Trails leaving the board (a Respawn round, core respawn.js): a segment has
    // `clearedAt`, and a "trail_cleared" event says whose colour went when. A
    // segment is drawn until the render clock reaches its clear; then the baked
    // layer re-bakes without it and its shape plays out as pellets (trail-fade.js).
    var clearClock = -Infinity; // the render time trails are judged against
    var clears = [];            // the trail_cleared events read so far, in time order
    var clearsRead = 0;         // how far into gameEvents
    var clearsPassed = 0;       // how many of `clears` the render clock has passed
    var fading = [];            // the pellets playing: trail-fade.js entries
    function isGone(seg) {
        return seg.clearedAt !== undefined && seg.clearedAt <= clearClock;
    }
    function startPellets(clear) {
        var bp = players.find(function (p) { return p.id === clear.playerId; });
        var color = bp ? wormColors[bp.colorId] : "#888";
        forEach(gameState.wormPathSegments, function (segments) {
            var run = [];
            function leave() {
                if (run.length) {
                    var tp = trailPoints(run);
                    if (tp.len > 0) fading.push({ at: clear.time, color: color, size: run[run.length - 1].size, points: tp.points, dist: tp.dist, len: tp.len });
                }
                run = [];
            }
            for (var i = 0; i < segments.length; i++) {
                var seg = segments[i];
                if (seg.clearedAt === clear.time && seg.playerId === clear.playerId) run.push(seg);
                else leave();
            }
            leave();
        });
    }

    function getWormRenderData(wormId) {
        if (wormRenderData[wormId] === undefined) {
            wormRenderData[wormId] = {
                mainSegmentIndex: 0
            };
        }
        return wormRenderData[wormId];
    }

    function clearRenderData() {
        forEach(wormRenderData, function (renderData) {
            renderData.mainSegmentIndex = 0;
            renderData.bakedWhole = false;
            renderData.lastMainPoint = null; // re-bake starts a fresh polyline chain
        });
    }

    // Draw segments [from..to] of `wormId` as CONTINUOUS polyline runs on `context`. A run is one
    // stroke (round join + cap); it breaks on a colour change (switcharoonie turnover), a size change,
    // a no-draw segment (jump / still_arc / speed 0), or a wall-wrap gap. `clampTime` clamps the LAST
    // segment to a live clock (the leading edge; undefined = full geometry). `initialPrev` is the point
    // to start the first run from (so a bake connects seamlessly to the previously baked point, and the
    // live tail connects to the baked trail). Returns the final drawn point (for the next bake to chain).
    // `dirty` (optional): a {minX,minY,maxX,maxY,maxSize} accumulator (GAME units) grown to cover
    // every drawn point - the per-frame tail draw feeds it into the dynamic layer's dirty rects.
    function drawRuns(context, sc, wormId, from, to, clampTime, initialPrev, dirty) {
        var segs = gameState.wormPathSegments[wormId];
        var open = false, runColor = null, runSize = 0, prevEnd = initialPrev || null;
        function closeRun() { if (open) { context.stroke(); open = false; } }
        function acc(x, y) {
            if (!dirty) return;
            if (x < dirty.minX) dirty.minX = x;
            if (x > dirty.maxX) dirty.maxX = x;
            if (y < dirty.minY) dirty.minY = y;
            if (y > dirty.maxY) dirty.maxY = y;
        }
        for (var i = from; i <= to && i < segs.length; i++) {
            var seg = segs[i];
            if (isFreeCheckpoint(seg) || isGone(seg)) { closeRun(); prevEnd = null; continue; }
            var bp = players.find(function (p) { return p.id === seg.playerId; });
            if (!bp) { closeRun(); prevEnd = null; continue; }
            var col = wormColors[bp.colorId];
            var endFrac = 1;
            if (i === to && clampTime !== undefined && seg.duration > 0) {
                endFrac = Math.max(0, Math.min(1, (clampTime - seg.startTime) / seg.duration));
            }
            if (endFrac <= 0) continue;
            if (dirty && seg.size > dirty.maxSize) dirty.maxSize = seg.size;
            var wrap = prevEnd && Math.hypot(seg.startX - prevEnd.x, seg.startY - prevEnd.y) > 20;
            if (!open || col !== runColor || seg.size !== runSize || wrap) {
                closeRun();
                context.lineWidth = scale * seg.size * 2;
                context.lineCap = "round"; context.lineJoin = "round";
                context.strokeStyle = col;
                context.beginPath();
                if (prevEnd && !wrap) { sc.moveTo(prevEnd.x, prevEnd.y); acc(prevEnd.x, prevEnd.y); } else { sc.moveTo(seg.startX, seg.startY); acc(seg.startX, seg.startY); }
                open = true; runColor = col; runSize = seg.size;
            }
            if (seg.type === "arc") {
                var sweep = endFrac * seg.arcAngleDiff;
                var steps = Math.max(1, Math.ceil(Math.abs(sweep) / 0.1));
                for (var s = 1; s <= steps; s++) {
                    var a = seg.arcStartAngle + (s / steps) * sweep;
                    var px = seg.arcCenterX + seg.arcRadius * Math.cos(a);
                    var py = seg.arcCenterY + seg.arcRadius * Math.sin(a);
                    sc.lineTo(px, py);
                    acc(px, py);
                }
                var ea = seg.arcStartAngle + sweep;
                prevEnd = { x: seg.arcCenterX + seg.arcRadius * Math.cos(ea), y: seg.arcCenterY + seg.arcRadius * Math.sin(ea) };
            } else {
                acc(seg.startX, seg.startY);
                var ex = seg.startX + endFrac * (seg.endX - seg.startX);
                var ey = seg.startY + endFrac * (seg.endY - seg.startY);
                sc.lineTo(ex, ey);
                acc(ex, ey);
                prevEnd = { x: ex, y: ey };
            }
        }
        closeRun();
        return prevEnd;
    }

    function render(renderTime) {
        if (renderTime === prevRenderTime) {
            return;
        }
        // NOTE: the secondary canvas is the ONE per-frame dynamic layer now: the shared
        // head renderer, the predicted own-worm renderer (head/FX/ghost stubs) and the
        // switch animation all draw onto it later in the same frame, relying on this
        // clear having run first. Keep this renderer ordered before them all, and keep
        // their same-clock skip guards in sync with the early-return above.
        //
        // Dirty-rect clearing: everything on this layer is per-frame content, so erasing
        // the union of LAST frame's drawn rects wipes the whole layer's content without
        // invalidating the entire fullscreen texture (which made the GPU re-raster it
        // every frame). Each renderer that draws here pushes the PIXEL rects it drew
        // into canvasState.dyn.cur, or sets dyn.full for FX it can't bound (arrows,
        // spawn pulses, victory anims, the switch animation, debug overlays) - a full
        // clear then runs NEXT frame. GameCanvas.update rolls cur into prev per frame.
        var dyn = canvasState.dyn || (canvasState.dyn = { prev: null, cur: [], full: false });
        if (renderTime < prevRenderTime) {
            // TODO Perform this more efficiently
            mainContext.clearRect(0, 0, mainCanvas.width, mainCanvas.height);
            clearRenderData();
            dyn.prev = null; // rewind: stale pixels may sit outside any tracked rect
        }
        if (!dyn.prev || dyn.prev.full || dyn.prev.rects.length > 48) {
            secondaryContext.clearRect(0, 0, secondaryCanvas.width, secondaryCanvas.height);
        } else {
            var prs = dyn.prev.rects;
            for (var pi = 0; pi < prs.length; pi++) {
                secondaryContext.clearRect(prs[pi][0], prs[pi][1], prs[pi][2], prs[pi][3]);
            }
        }
        dyn.cur = [];
        dyn.full = false;

        // Retraction re-bake: a worm whose baked length dropped below our cursor had its trail
        // SHORTENED by the server (a crash pulling an over-predicted trail back to the real death
        // point). The persistent canvas then holds pixels for segments that no longer exist, so we
        // re-bake from scratch. This is now the ONLY full re-bake, and it's rare (a death). A
        // switcharoonie no longer replaces or shrinks any track (clients append new segments by time,
        // the server never resyncs old ones), so switches don't trip this: NO re-render of old parts
        // on a switch. A worm handed from your-own to opponent is instead re-baked additively, per
        // worm, in the draw loop below (no canvas clear, so no other worm is disturbed).
        var shrank = false;
        forEach(gameState.wormPathSegments, function (segments, trackId) {
            var rd = wormRenderData[trackId];
            // (A track baked whole, see isFinishedTrack, legitimately sits AT its length.)
            if (rd && segments.length && rd.mainSegmentIndex >= segments.length + (rd.bakedWhole ? 1 : 0)) shrank = true;
        });
        // A colour taken off the board (see clearClock): the baked layer still
        // holds its pixels, so it re-bakes from what is left, once.
        var events = gameState.gameEvents || [];
        for (; clearsRead < events.length; clearsRead++) {
            if (events[clearsRead].type === "trail_cleared") clears.push(events[clearsRead]);
        }
        clearClock = renderTime;
        if (renderTime < prevRenderTime) {
            // rewound (a replay): only the clears up to here count, and none plays again
            fading = [];
            clearsPassed = 0;
            while (clearsPassed < clears.length && clears[clearsPassed].time <= renderTime) clearsPassed++;
        }
        while (clearsPassed < clears.length && clears[clearsPassed].time <= renderTime) {
            if (renderTime - clears[clearsPassed].time < TRAIL_FADE_SECONDS) startPellets(clears[clearsPassed]);
            clearsPassed++;
            shrank = true;
        }
        if (shrank) {
            mainContext.clearRect(0, 0, mainCanvas.width, mainCanvas.height);
            clearRenderData();
        }


        // Now render normally. This shared renderer draws the WHOLE trail of EVERY worm - your own
        // included, wall-hack ghost tracks too, all the way to each track's per-worm render clock
        // (the predicted "now" for the own worm AND its "#" ghost tracks, so a mirror stub never
        // lags the head) - by this one algorithm. So a worm's trail never re-strokes (and never
        // changes smoothness) when a switcharoonie hands it between owners; only the heads are
        // drawn elsewhere, always after the trails.
        var nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
        var bakeDue = nowMs - lastBakeAt >= BAKE_INTERVAL_MS;
        if (bakeDue) lastBakeAt = nowMs;
        forEach(gameState.wormPathSegments, function (segments, wormId) {
            var renderData = getWormRenderData(wormId);
            // Per-opponent adaptive delay: draw this worm's leading edge at its own
            // (latency-sized) render time. Falls back to the shared frame time.
            var wt = renderTime;
            if (getWormRenderTime && segments[0]) {
                var t = getWormRenderTime(wormId);
                if (typeof t === "number") wt = t;
            }
            // Roll call: hold this worm's trail until its head is fully in.
            if (introDelay && segments[0] && introAlpha(wt, introDelay(segments[0].playerId), gameState.startPhaseTime) < 1) {
                return;
            }
            if (segments.length > 0) {
                // Find the last FINALIZED segment (its end is behind the render clock, or its successor
                // has already started - the stall guard that keeps a corrupt server-stamped endTime from
                // freezing the cursor and hiding the rest of a taken-over worm's trail).
                var lastFinal = renderData.mainSegmentIndex - 1;
                for (var fi = renderData.mainSegmentIndex; fi < segments.length - 1 &&
                        (segments[fi].endTime <= wt || segments[fi + 1].startTime <= wt); fi++) {
                    lastFinal = fi;
                }
                // Batched (see BAKE_INTERVAL_MS): outside a bake frame, only a tail past MAX_TAIL bakes.
                var bakeHere = bakeDue || lastFinal - renderData.mainSegmentIndex + 1 >= MAX_TAIL;
                // A finished track (dead worm, idle wrap track) bakes whole, last segment included,
                // and then draws nothing per frame until it grows again.
                if (bakeDue && renderData.mainSegmentIndex < segments.length && isFinishedTrack(segments, wormId, wt)) {
                    renderData.lastMainPoint = drawRuns(mainContext, scaledMainContext, wormId,
                        renderData.mainSegmentIndex, segments.length - 1, undefined, renderData.lastMainPoint);
                    renderData.mainSegmentIndex = segments.length;
                    renderData.bakedWhole = true;
                }
                if (renderData.mainSegmentIndex >= segments.length) {
                    return;
                }
                renderData.bakedWhole = false; // it grew again: back to the normal tail
                // Free checkpoint first: if a jump/still_arc/zero-speed segment finalized since our last
                // bake, flush straight through the MOST RECENT one in one stroke - no seam cost (see
                // isFreeCheckpoint), and it shrinks the backlog without waiting for a full CHUNK.
                for (var bi = lastFinal; bakeHere && bi >= renderData.mainSegmentIndex; bi--) {
                    if (isFreeCheckpoint(segments[bi])) {
                        renderData.lastMainPoint = drawRuns(mainContext, scaledMainContext, wormId,
                            renderData.mainSegmentIndex, bi, undefined, renderData.lastMainPoint);
                        renderData.mainSegmentIndex = bi + 1;
                        break;
                    }
                }
                // Flush any remaining finalized segments to the PERSISTENT main canvas a whole CHUNK at a
                // time, each chunk one continuous stroke (few seams) - the fallback for stretches with no
                // free checkpoint to lean on.
                while (bakeHere && lastFinal - renderData.mainSegmentIndex + 1 >= TRAIL_CHUNK) {
                    renderData.lastMainPoint = drawRuns(mainContext, scaledMainContext, wormId,
                        renderData.mainSegmentIndex, renderData.mainSegmentIndex + TRAIL_CHUNK - 1, undefined, renderData.lastMainPoint);
                    renderData.mainSegmentIndex += TRAIL_CHUNK;
                }
                // Draw the un-baked tail (recent finalized segments + the current one, clamped to the
                // render clock) live on the secondary canvas each frame, as continuous polylines that
                // connect seamlessly to the baked trail (starting from lastMainPoint).
                var tailEnd = Math.min(segments.length - 1, lastFinal + 1);
                var dirty = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity, maxSize: 0 };
                drawRuns(secondaryContext, scaledSecondaryContext, wormId,
                    renderData.mainSegmentIndex, tailEnd, wt, renderData.lastMainPoint, dirty);
                if (dirty.minX !== Infinity) {
                    // Game units -> pixels, padded by the stroke's half-width (size) + AA slack.
                    var dpad = dirty.maxSize * scale + 3;
                    dyn.cur.push([
                        dirty.minX * scale - dpad, dirty.minY * scale - dpad,
                        (dirty.maxX - dirty.minX) * scale + dpad * 2, (dirty.maxY - dirty.minY) * scale + dpad * 2,
                    ]);
                }
            }
        });

        // The pellets of trails leaving the board, over the others on the per-frame layer.
        if (fading.length) {
            fading = fading.filter(function (f) { return renderTime - f.at < TRAIL_FADE_SECONDS; });
            for (var ff = 0; ff < fading.length; ff++) {
                drawTrailFade(secondaryContext, scaledSecondaryContext, scale, fading[ff], (renderTime - fading[ff].at) / TRAIL_FADE_SECONDS);
                dyn.full = true;
            }
        }

        prevRenderTime = renderTime;
    }

    return {
        render: render
    };
};
